import {
  ANALYSIS_SETTINGS_FILE_SECTIONS,
  AnalysisSettings,
  AnalysisSettingsSectionKey,
  buildDefaultAnalysisSettings,
} from "./analysisSettingsDefaults";

export const ANALYSIS_TYPE_FILENAME = "analysis-type.json";
export const ANALYSIS_SETTINGS_ZIP_FILENAME = "analysis-settings.zip";

const ANALYSIS_SETTINGS_INI_SECTION_NAMES: Record<Exclude<AnalysisSettingsSectionKey, "modules">, string> = {
  beatDetection: "BeatDetection",
  rhythmAnalysis: "RhythmAnalysis",
  qtAnalysisOptions: "QTAnalysisOptions",
};

export interface StudySettingsFile {
  fileName: string;
  blob: Blob;
}

export interface StudySettingsFiles {
  analysisType: StudySettingsFile;
  analysisSettings: StudySettingsFile;
}

const textToBytes = (value: string) => new Uint8Array(value.split("").map((char) => char.charCodeAt(0)));
const bytesToText = (bytes: Uint8Array) => String.fromCharCode(...Array.from(bytes));

const makeCrcTable = () => {
  const table: number[] = [];

  for (let index = 0; index < 256; index += 1) {
    let value = index;

    for (let bit = 0; bit < 8; bit += 1) {
      value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
    }

    table[index] = value >>> 0;
  }

  return table;
};

const CRC_TABLE = makeCrcTable();

const crc32 = (bytes: Uint8Array) => {
  let crc = 0xffffffff;

  bytes.forEach((byte) => {
    crc = CRC_TABLE[(crc ^ byte) & 0xff] ^ (crc >>> 8);
  });

  return (crc ^ 0xffffffff) >>> 0;
};

const writeUint16 = (target: number[], value: number) => {
  target.push(value & 0xff, (value >>> 8) & 0xff);
};

const writeUint32 = (target: number[], value: number) => {
  target.push(value & 0xff, (value >>> 8) & 0xff, (value >>> 16) & 0xff, (value >>> 24) & 0xff);
};

const readUint16 = (bytes: Uint8Array, offset: number) => bytes[offset] | (bytes[offset + 1] << 8);

const readUint32 = (bytes: Uint8Array, offset: number) =>
  (bytes[offset] | (bytes[offset + 1] << 8) | (bytes[offset + 2] << 16) | (bytes[offset + 3] << 24)) >>> 0;

const appendBytes = (target: number[], bytes: Uint8Array) => {
  bytes.forEach((byte) => target.push(byte));
};

const readBlobAsUint8Array = (blob: Blob) => new Promise<Uint8Array>((resolve, reject) => {
  if ("arrayBuffer" in blob) {
    blob.arrayBuffer()
      .then((buffer) => resolve(new Uint8Array(buffer)))
      .catch(reject);
    return;
  }

  const reader = new FileReader();

  reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
  reader.onerror = () => reject(reader.error);
  reader.readAsArrayBuffer(blob);
});

const createStoredZipBlob = async (files: StudySettingsFile[]) => {
  const output: number[] = [];
  const centralDirectory: number[] = [];

  for (const file of files) {
    const fileNameBytes = textToBytes(file.fileName);
    const contentBytes = await readBlobAsUint8Array(file.blob);
    const checksum = crc32(contentBytes);
    const localHeaderOffset = output.length;

    writeUint32(output, 0x04034b50);
    writeUint16(output, 20);
    writeUint16(output, 0);
    writeUint16(output, 0);
    writeUint16(output, 0);
    writeUint16(output, 0);
    writeUint32(output, checksum);
    writeUint32(output, contentBytes.length);
    writeUint32(output, contentBytes.length);
    writeUint16(output, fileNameBytes.length);
    writeUint16(output, 0);
    appendBytes(output, fileNameBytes);
    appendBytes(output, contentBytes);

    writeUint32(centralDirectory, 0x02014b50);
    writeUint16(centralDirectory, 20);
    writeUint16(centralDirectory, 20);
    writeUint16(centralDirectory, 0);
    writeUint16(centralDirectory, 0);
    writeUint16(centralDirectory, 0);
    writeUint16(centralDirectory, 0);
    writeUint32(centralDirectory, checksum);
    writeUint32(centralDirectory, contentBytes.length);
    writeUint32(centralDirectory, contentBytes.length);
    writeUint16(centralDirectory, fileNameBytes.length);
    writeUint16(centralDirectory, 0);
    writeUint16(centralDirectory, 0);
    writeUint16(centralDirectory, 0);
    writeUint16(centralDirectory, 0);
    writeUint32(centralDirectory, 0);
    writeUint32(centralDirectory, localHeaderOffset);
    appendBytes(centralDirectory, fileNameBytes);
  }

  const centralDirectoryOffset = output.length;
  output.push(...centralDirectory);

  writeUint32(output, 0x06054b50);
  writeUint16(output, 0);
  writeUint16(output, 0);
  writeUint16(output, files.length);
  writeUint16(output, files.length);
  writeUint32(output, centralDirectory.length);
  writeUint32(output, centralDirectoryOffset);
  writeUint16(output, 0);

  return new Blob([new Uint8Array(output)], { type: "application/zip" });
};

const buildIniFileContent = (
  settings: AnalysisSettings,
  sectionKey: Exclude<AnalysisSettingsSectionKey, "modules">
) => {
  const section = ANALYSIS_SETTINGS_FILE_SECTIONS.find((item) => item.key === sectionKey);

  if (!section) return "";

  const lines = [`[${ANALYSIS_SETTINGS_INI_SECTION_NAMES[sectionKey]}]`];

  section.fields.forEach((field) => {
    lines.push(`${field.name}=${settings[sectionKey][field.name]}`);
  });

  return `${lines.join("\n")}\n`;
};

const parseIniFileContent = (settings: AnalysisSettings, sectionKey: Exclude<AnalysisSettingsSectionKey, "modules">, content: string) => {
  content
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line && !line.startsWith("["))
    .forEach((line) => {
      const separatorIndex = line.indexOf("=");

      if (separatorIndex === -1) return;

      const key = line.substring(0, separatorIndex).trim();
      const value = line.substring(separatorIndex + 1).trim();

      if (key) {
        settings[sectionKey][key] = value;
      }
    });
};

const decompressDeflatedBytes = async (bytes: Uint8Array) => {
  const DecompressionStreamCtor = (globalThis as any).DecompressionStream;

  if (!DecompressionStreamCtor) {
    throw new Error("Compressed ZIP files are not supported by this browser");
  }

  const stream = (new Blob([bytes]).stream() as any).pipeThrough(new DecompressionStreamCtor("deflate-raw"));
  const blob = await new Response(stream).blob();

  return await readBlobAsUint8Array(blob);
};

const readZipFiles = async (zipBlob: Blob) => {
  const bytes = await readBlobAsUint8Array(zipBlob);
  const files: Record<string, string> = {};
  let offset = 0;

  while (offset + 30 <= bytes.length && readUint32(bytes, offset) === 0x04034b50) {
    const compressionMethod = readUint16(bytes, offset + 8);
    const compressedSize = readUint32(bytes, offset + 18);
    const fileNameLength = readUint16(bytes, offset + 26);
    const extraFieldLength = readUint16(bytes, offset + 28);
    const fileNameStart = offset + 30;
    const fileNameEnd = fileNameStart + fileNameLength;
    const dataStart = fileNameEnd + extraFieldLength;
    const dataEnd = dataStart + compressedSize;
    const fileName = bytesToText(bytes.slice(fileNameStart, fileNameEnd));
    const fileBytes = bytes.slice(dataStart, dataEnd);
    const contentBytes = compressionMethod === 0
      ? fileBytes
      : await decompressDeflatedBytes(fileBytes);

    files[fileName] = bytesToText(contentBytes);
    offset = dataEnd;
  }

  return files;
};

export const buildAnalysisTypeJsonBlob = (modules: AnalysisSettings["modules"]) =>
  new Blob([JSON.stringify(modules, null, 2)], { type: "application/json" });

export const parseAnalysisTypeJsonBlob = async (blob: Blob): Promise<AnalysisSettings["modules"]> => {
  const content = bytesToText(await readBlobAsUint8Array(blob));
  const parsedModules = JSON.parse(content);
  const defaultSettings = buildDefaultAnalysisSettings();

  return {
    ...defaultSettings.modules,
    ...parsedModules,
  };
};

export const buildAnalysisSettingsZipBlob = async (settings: AnalysisSettings) => createStoredZipBlob([
  {
    fileName: "AnalysisModuleOptions.ini",
    blob: new Blob([buildIniFileContent(settings, "beatDetection")], { type: "text/plain" }),
  },
  {
    fileName: "ArrhythmiaAnalysisModuleOptions.ini",
    blob: new Blob([buildIniFileContent(settings, "rhythmAnalysis")], { type: "text/plain" }),
  },
  {
    fileName: "BeatToBeatModuleOptions.ini",
    blob: new Blob([buildIniFileContent(settings, "qtAnalysisOptions")], { type: "text/plain" }),
  },
  {
    fileName: "Ver.txt",
    blob: new Blob([`${settings.version}\n`], { type: "text/plain" }),
  },
]);

export const buildStudySettingsFiles = async (settings: AnalysisSettings): Promise<StudySettingsFiles> => ({
  analysisType: {
    fileName: ANALYSIS_TYPE_FILENAME,
    blob: buildAnalysisTypeJsonBlob(settings.modules),
  },
  analysisSettings: {
    fileName: ANALYSIS_SETTINGS_ZIP_FILENAME,
    blob: await buildAnalysisSettingsZipBlob(settings),
  },
});

export const parseAnalysisSettingsZipBlob = async (blob: Blob): Promise<AnalysisSettings> => {
  const settings = buildDefaultAnalysisSettings();
  const files = await readZipFiles(blob);

  if (files["AnalysisModuleOptions.ini"]) {
    parseIniFileContent(settings, "beatDetection", files["AnalysisModuleOptions.ini"]);
  }

  if (files["ArrhythmiaAnalysisModuleOptions.ini"]) {
    parseIniFileContent(settings, "rhythmAnalysis", files["ArrhythmiaAnalysisModuleOptions.ini"]);
  }

  if (files["BeatToBeatModuleOptions.ini"]) {
    parseIniFileContent(settings, "qtAnalysisOptions", files["BeatToBeatModuleOptions.ini"]);
  }

  if (files["Ver.txt"]?.trim()) {
    settings.version = files["Ver.txt"].trim();
  }

  return settings;
};

export const downloadBlob = (fileName: string, blob: Blob) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");

  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
};
